import {
  formatDateRangeLabel,
  useDateRangeFilter,
} from "@/components/observe/useDateRangeFilter";
import { Page } from "@/components/page-layout";
import { RequireScope } from "@/components/require-scope";
import { encodeIdentityUrn } from "@/lib/identity-urn";
import { useRoutes } from "@/routes";
import { type DateRangePreset } from "@/elements";
import { TimeRangePicker } from "@/components/DashboardTimeRangePicker";
import { useRiskOverview } from "@gram/client/react-query/riskOverview.js";
import { Icon } from "@/components/ui/Icon";
import { useMemo } from "react";
import { Link, useLocation } from "react-router";

const RISK_OVERVIEW_PRESETS: DateRangePreset[] = [
  "15m",
  "1h",
  "4h",
  "1d",
  "2d",
  "3d",
  "7d",
  "15d",
  "30d",
];

export default function RiskOverviewUsersIndex(): JSX.Element {
  return (
    <RequireScope scope="org:admin" level="page">
      <Page>
        <Page.Header>
          <Page.Header.Breadcrumbs />
        </Page.Header>
        <Page.Body>
          <RiskOverviewUsersIndexContent />
        </Page.Body>
      </Page>
    </RequireScope>
  );
}

function RiskOverviewUsersIndexContent() {
  const routes = useRoutes();
  const location = useLocation();
  const {
    dateRange,
    customRange,
    customRangeLabel,
    from,
    to,
    setDateRangeParam,
    setCustomRangeParam,
    clearCustomRange,
  } = useDateRangeFilter();
  const rangeLabel = useMemo(
    () => formatDateRangeLabel(dateRange, customRangeLabel),
    [dateRange, customRangeLabel],
  );

  const overviewQuery = useRiskOverview({ from, to });
  const users = overviewQuery.data?.topUsers ?? [];
  const total = users.reduce((acc, u) => acc + Number(u.findings), 0);
  const max = users[0]?.findings ?? 0;

  const controls = (
    <TimeRangePicker
      preset={customRange ? null : dateRange}
      customRange={customRange}
      customRangeLabel={customRangeLabel}
      availablePresets={RISK_OVERVIEW_PRESETS}
      onPresetChange={setDateRangeParam}
      onCustomRangeChange={setCustomRangeParam}
      onClearCustomRange={clearCustomRange}
    />
  );

  return (
    <Page.Section>
      <Page.Section.Title stage="beta">Users</Page.Section.Title>
      <Page.Section.Description>
        All users with finding counts
        {rangeLabel && ` across ${rangeLabel}.`}
      </Page.Section.Description>
      <Page.Section.CTA>{controls}</Page.Section.CTA>
      <Page.Section.Body>
        {overviewQuery.isLoading ? (
          <div className="text-muted-foreground flex items-center justify-center gap-2 py-12">
            <Icon name="loader-circle" className="size-5 animate-spin" />
            <span>Loading users...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="bg-muted flex size-12 items-center justify-center rounded-full">
              <Icon name="inbox" className="text-muted-foreground size-6" />
            </div>
            <span className="text-foreground font-medium">
              No users with findings in this time range
            </span>
          </div>
        ) : (
          <ul className="divide-border divide-y border">
            {users.map((u, i) => {
              // Rows lead to the identity page rather than to the risk-only
              // user view: one person's findings, spend and access now hang
              // off a single page.
              // Findings resolved through the directory carry an address and
              // no external id; the resolver keys on either, so both reach the
              // person's page.
              const identityUrn = u.externalUserId
                ? `external:${u.externalUserId}`
                : u.email
                  ? `email:${u.email}`
                  : null;
              const href = identityUrn
                ? `${routes.identities.detail.overview.href(
                    encodeIdentityUrn(identityUrn),
                  )}${location.search}`
                : null;
              const pct =
                max > 0 ? (Number(u.findings) / Number(max)) * 100 : 0;
              const totalPct =
                total > 0 ? (Number(u.findings) / total) * 100 : 0;
              const body = (
                <div className="flex items-center gap-4 px-4 py-3">
                  <span className="text-muted-foreground w-6 shrink-0 text-right text-xs">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium normal-case">
                        {u.email || u.externalUserId || "Unknown user"}
                      </span>
                      <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                        {Number(u.findings).toLocaleString()}
                        {total > 0 && (
                          <span className="ml-2">({totalPct.toFixed(1)}%)</span>
                        )}
                      </span>
                    </div>
                    <div className="bg-muted h-1 w-full">
                      <div
                        className="bg-destructive h-1"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {u.externalUserId && u.externalUserId !== u.email && (
                      <div className="text-muted-foreground mt-1 truncate font-mono text-xs">
                        {u.externalUserId}
                      </div>
                    )}
                  </div>
                  {href && (
                    <Icon
                      name="chevron-right"
                      className="text-muted-foreground size-4 shrink-0"
                    />
                  )}
                </div>
              );
              return (
                <li key={u.externalUserId || u.email || i}>
                  {href ? (
                    <Link to={href} className="hover:bg-muted/40 block">
                      {body}
                    </Link>
                  ) : (
                    body
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Page.Section.Body>
    </Page.Section>
  );
}
